/**
 * Floating Actions Registry
 *
 * Simple registry for managing floating action sections (page, dashboard, etc.)
 * Following the pluggable view pattern for simplicity.
 */
/**
 * @export
 */
export var FloatingActionSectionType;
(function (FloatingActionSectionType) {
    FloatingActionSectionType["Page"] = "page";
    FloatingActionSectionType["Dashboard"] = "dashboard";
})(FloatingActionSectionType || (FloatingActionSectionType = {}));
/**
 * @export
 */
export var FloatingActionButtonVisibility;
(function (FloatingActionButtonVisibility) {
    FloatingActionButtonVisibility["Always"] = "always";
    FloatingActionButtonVisibility["Normal"] = "normal";
    FloatingActionButtonVisibility["Edit"] = "edit";
})(FloatingActionButtonVisibility || (FloatingActionButtonVisibility = {}));
/**
 * Global registry storing all floating action sections
 */
const registry = new Map();
const listeners = [];
/**
 * Notify all listeners of registry changes
 */
function notifyChange() {
    listeners.forEach(listener => listener());
}
/**
 * @export
 * Register a floating action section
 *
 * section - Section configuration
 *
 * @example
 * ```typescript
 * registerFloatingActionSection({
 *   id: 'page',
 *   type: 'page',
 *   icon: 'phl pencil',
 *   label: 'Edit Page',
 *   isActive: isEditing,
 *   onToggle: () => setIsEditing(!isEditing),
 *   render: () => <ButtonComponent ... />
 * });
 * ```
 */
export function registerFloatingActionSection(section) {
    registry.set(section.id, section);
    notifyChange();
}
/**
 * @export
 * Unregister a floating action section
 *
 * @param id - Section ID to remove
 */
export function unregisterFloatingActionSection(id) {
    registry.delete(id);
    notifyChange();
}
/**
 * Get all registered floating action sections
 *
 * @returns Array of all sections
 */
export function getAllSections() {
    return Array.from(registry.values());
}
/**
 * Subscribe to registry changes
 *
 * @param listener - Callback function to execute on changes
 * @returns Unsubscribe function
 */
export function onRegistryChange(listener) {
    listeners.push(listener);
    return () => {
        const index = listeners.indexOf(listener);
        if (index > -1) {
            listeners.splice(index, 1);
        }
    };
}
/**
 * Clear all sections
 * Useful for testing
 */
export function clearAllSections() {
    registry.clear();
    notifyChange();
}
